import React, { useState, useEffect, useCallback } from "react";
import Styles from "./Countdown.module.css";
import { ClockIcon } from "@mui/x-date-pickers";
import { formatDateTimeView } from "../FormatDate";
import { ReactComponent as Clock } from "../../assets/images/clock.svg";

const Countdown = ({
  status,
  tenderData,
  type,
  isBufferTime,
  bufferEndTime,
}) => {
  // Determine heading and dateTime based on status and tenderData
  const heading = status.includes("tender_closing_soon")
    ? "Tender Closing in"
    : status.includes("ra_ongoing")
    ? "Auction Ends in"
    : status.includes("ra_upcoming")
    ? "Auction Starts in"
    : status.includes("cb_ongoing")
    ? "Bidding Ends in"
    : status.includes("cb_upcoming")
    ? "Bidding Starts in"
    : status.includes("upcoming")
    ? "Tender Opens in"
    : status.includes("closed")
    ? "Tender Ended"
    : status.length === 0
    ? "Tender Ended"
    : "Tender Ended";

  // Determine if endState CSS should be used
  const useEndState =
    heading === "Tender Closing in" ||
    heading === "Bidding Ends in" ||
    heading === "Auction Ends in";

  const dateTime = status.includes("tender_closing_soon")
    ? formatDateTimeView(tenderData?.tenderCloseDateTime)
    : status.includes("ra_ongoing")
    ? formatDateTimeView(tenderData?.auctionCloseDateTime)
    : status.includes("ra_upcoming")
    ? formatDateTimeView(tenderData?.auctionStartDateTime)
    : status.includes("cb_ongoing")
    ? formatDateTimeView(tenderData?.bidCloseDateTime)
    : status.includes("cb_upcoming")
    ? formatDateTimeView(tenderData?.bidStartDateTime)
    : status.includes("upcoming")
    ? formatDateTimeView(tenderData?.tenderStartDateTime)
    : null;

  const calculateTimeLeft = useCallback(() => {
    // Handle buffer time first
    if (isBufferTime && bufferEndTime) {
      const now = new Date();
      const target = new Date(bufferEndTime);
      const difference = target - now;
      if (difference <= 0) return null;
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        buffer: true,
      };
    }

    // Handle regular countdown
    if (!dateTime || status === "closed") {
      return null;
    }

    const now = new Date();
    const target = new Date(dateTime);
    const difference = target - now;
    if (difference <= 0) {
      return null; // Expired
    }

    const timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };

    return timeLeft;
  }, [isBufferTime, bufferEndTime, dateTime, status]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    // For buffer time, we don't need dateTime check
    if (!isBufferTime && (!dateTime || status === "closed")) {
      setTimeLeft(null);
      return;
    }

    const timer = setInterval(() => {
      const updatedTimeLeft = calculateTimeLeft();
      setTimeLeft(updatedTimeLeft);
    }, 1000);

    return () => clearInterval(timer); // Cleanup on unmount
  }, [dateTime, status, isBufferTime, bufferEndTime, calculateTimeLeft]);

  // Only show "Expired" if it's not buffer time or if buffer time is also expired
  if (!isBufferTime && (status === "closed" || !timeLeft)) {
    return <div className={Styles.headExpiry}>Tender Ended</div>;
  }

  // If it's buffer time and timeLeft is null, then buffer time has expired
  if (isBufferTime && !timeLeft) {
    return <div className={Styles.headExpiry}>Buffer Time Expired</div>;
  }

  if (timeLeft?.buffer) {
    return (
      <div className={Styles.countdownMainSelf}>
        <div className={Styles.heading_text_bid_remaining}>Remaining Time</div>
        <div className={Styles.timeLeft}>
          <span>
            <Clock />
          </span>{" "}
          {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m{" "}
          {timeLeft.seconds}s
        </div>
      </div>
    );
  }

  return (
    <>
      {type === "bid" ? (
        <div className={`${Styles.countdownMain_type_bid}`}>
          <div
            className={`${Styles.heading_text_bid} ${
              useEndState && Styles.endState
            }`}
          >
            {heading}
          </div>
          {heading !== "Tender Ended" && (
            <div
              className={`display_flex_row ${Styles.timeLeft_bid} ${
                useEndState && Styles.endStateElement
              }`}
            >
              <ClockIcon style={{ fontSize: "12px" }} />
              <span>
                <b>{timeLeft.days}</b> d{"  "} <b>{timeLeft.hours}</b> h{"  "}{" "}
                <b>{timeLeft.minutes}</b> m{"  "} <b>{timeLeft.seconds}</b> s
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className={`display_flex_row_gap_8_only ${Styles.countdownMain}`}>
          <div
            className={`${Styles.heading_text_bid} ${
              useEndState && Styles.endState
            }`}
          >
            {heading}
          </div>
          {heading !== "Tender Ended" && (
            <div
              className={`${Styles.timeLeft} ${
                useEndState && Styles.endStateElement
              }`}
            >
              <ClockIcon style={{ fontSize: "12px" }} />
              <span>
                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m{" "}
                {timeLeft.seconds}s
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Countdown;
